// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useMemo } from 'react'
import { FileText, List, Pencil, Table, Text, Trash2 } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  CardContent,
} from '@/components/ui'

import type { FormatType } from './FormatSpecEditor'
import { ReportSectionForm, type ReportSectionFormData } from './ReportSectionForm'

export interface ReportSectionItem {
  id: string
  title: string
  content: string
  formatType: FormatType
  formatSpec: string[]
  formatGuidance: string
}

export interface ReportSectionListProps {
  items: ReportSectionItem[]
  disabled?: boolean
  readOnly?: boolean
  editingItem?: ReportSectionItem | null
  saving?: boolean
  onEdit: (item: ReportSectionItem) => void
  onDelete: (item: ReportSectionItem) => Promise<void>
  onSubmitEdit?: (data: ReportSectionFormData) => Promise<void>
  onCancelEdit?: () => void
  onError?: (message: string) => void
}

const FORMAT_TYPE_LABELS: Record<FormatType, string> = {
  table: 'Table',
  bullets: 'Bullets',
  prose: 'Prose',
}

const FORMAT_TYPE_ICONS: Record<FormatType, React.ElementType> = {
  table: Table,
  bullets: List,
  prose: Text,
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength).trim() + '...'
}

/**
 * Card-based list of report sections.
 * Shows title, prompt preview, format type badge, and edit/delete actions.
 * Renders an inline edit form when a section is being edited.
 */
export function ReportSectionList({
  items,
  disabled = false,
  readOnly = false,
  editingItem,
  saving = false,
  onEdit,
  onDelete,
  onSubmitEdit,
  onCancelEdit,
  onError,
}: ReportSectionListProps): JSX.Element {
  const handleDelete = useCallback(async (item: ReportSectionItem) => {
    if (window.confirm(`Delete section "${item.title}"?`)) {
      await onDelete(item)
    }
  }, [onDelete])

  const editingFormData = useMemo(() => {
    if (!editingItem) return null
    return {
      title: editingItem.title,
      content: editingItem.content,
      formatType: editingItem.formatType,
      formatSpec: editingItem.formatSpec,
      formatGuidance: editingItem.formatGuidance,
    }
  }, [editingItem])

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/70 bg-muted/10 py-12 text-center" data-testid="report-sections-empty">
        <FileText className="h-10 w-10 mx-auto mb-3 text-slate-600/50" />
        <p className="text-sm text-slate-600">
          No report sections defined yet.
        </p>
        <p className="text-sm text-slate-600 mt-1">
          Use the Add Section button to define what the LLM should analyze.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3" data-testid="report-sections-list">
      {items.map((item, index) => {
        const isEditing = editingItem?.id === item.id

        if (isEditing && onSubmitEdit && onCancelEdit) {
          return (
            <ReportSectionForm
              key={item.id}
              disabled={disabled}
              saving={saving}
              initialData={editingFormData}
              onSubmit={onSubmitEdit}
              onCancel={onCancelEdit}
              onError={onError}
            />
          )
        }

        const FormatIcon = FORMAT_TYPE_ICONS[item.formatType]
        const formatLabel = FORMAT_TYPE_LABELS[item.formatType]
        const formatDetail = item.formatType === 'table' || item.formatType === 'bullets'
          ? item.formatSpec.length > 0 ? item.formatSpec.join(', ') : 'No columns/points defined'
          : item.formatGuidance || 'No guidance specified'

        return (
          <Card key={item.id} data-testid={`report-section-row-${item.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-slate-600 font-mono">
                      {index + 1}.
                    </span>
                    <h3 className="font-medium truncate">
                      {item.title}
                    </h3>
                    <Badge variant="secondary" className="flex items-center gap-1 shrink-0">
                      <FormatIcon className="h-3 w-3" />
                      {formatLabel}
                    </Badge>
                  </div>

                  <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                    {truncateText(item.content, 150)}
                  </p>

                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Format:</span>{' '}
                    {truncateText(formatDetail, 80)}
                  </p>
                </div>

                {!readOnly && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(item)}
                      disabled={disabled || !!editingItem}
                      className="h-8 px-2 text-slate-600 hover:text-foreground"
                      data-testid={`report-section-edit-${item.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      <span className="text-xs">Edit</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleDelete(item)}
                      disabled={disabled || !!editingItem}
                      className="h-8 px-2 text-slate-600 hover:text-destructive"
                      data-testid={`report-section-delete-${item.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      <span className="text-xs">Delete</span>
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
